import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createButton } from "../../components/ui/button";
import { createMcOptionTile } from "../../components/ui/mc-option-tile";
import { createField } from "../../components/ui/field";
import { createHeading } from "../../components/ui/heading";
import { createPageContainer } from "../../components/ui/page-container";
import { createSkipLink } from "../../components/ui/skip-link";
import { createStack } from "../../components/ui/stack";
import { createText } from "../../components/ui/text";
import { createTextInput } from "../../components/ui/text-input";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";
import {
  deleteParticipantAttempt,
  fetchParticipantAttempt,
  fetchParticipantBootstrap,
  participantTrainingDocumentDownloadUrl,
  patchParticipantAttemptSelection,
  postParticipantAttempt,
  postParticipantSubmit,
  type ParticipantAttemptSnapshot,
  type ParticipantBootstrap,
  type ParticipantTrainingDocument,
} from "../../lib/api-participant";
import {
  renderParticipantTranscriptReview,
  renderParticipantTranscriptSelectionOnly,
  type ParticipantTranscriptReviewRow,
} from "../organizer-quiz-transcript/transcript-shared";
import {
  createTrainingMaterialFileIcon,
  normalizedTrainingMaterialExtension,
  trainingMaterialIconKind,
} from "../../lib/training-material-file-icon";

export interface RenderParticipantQuizOptions {
  signal: AbortSignal;
  locale: SupportedLocale;
  quizId: string;
  invitationToken: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

const LAST_COMPLETED_SS_PREFIX =
  "participant-quiz:v1:last-completed-for-invite:";

function participantLastCompletedSessionKey(
  quizId: string,
  invitationToken: string,
): string {
  const tok = invitationToken.trim();
  let fold = 0;
  for (let i = 0; i < tok.length; i += 1) {
    fold = (Math.imul(31, fold) + tok.charCodeAt(i)) | 0;
  }
  return `${LAST_COMPLETED_SS_PREFIX}${quizId}:${tok.length}_${fold}`;
}

function storedLastAttemptId(quizId: string, invitationToken: string): string | null {
  try {
    const raw = sessionStorage.getItem(
      participantLastCompletedSessionKey(quizId, invitationToken),
    );
    const s = typeof raw === "string" ? raw.trim() : "";
    return s.length ? s : null;
  } catch {
    return null;
  }
}

function storeLastAttemptIdAfterSubmit(
  quizId: string,
  invitationToken: string,
  attemptId: string,
): void {
  try {
    sessionStorage.setItem(
      participantLastCompletedSessionKey(quizId, invitationToken),
      attemptId,
    );
  } catch {
    /* private mode quota */
  }
}

function clearStoredLastAttempt(quizId: string, invitationToken: string): void {
  try {
    sessionStorage.removeItem(
      participantLastCompletedSessionKey(quizId, invitationToken),
    );
  } catch {
    /* ignore */
  }
}

function isParsableParticipantAttempt(body: unknown): body is ParticipantAttemptSnapshot {
  if (!isRecord(body)) {
    return false;
  }
  if (
    typeof body.attempt_id !== "string" ||
    body.status !== "completed" ||
    !Array.isArray(body.ordered_questions)
  ) {
    return false;
  }
  if (!isRecord(body.answers)) {
    return false;
  }
  return true;
}

/** Restores **`last_completed_attempt`** when older APIs omit it from bootstrap (**GET** **`/participant/attempts/:id`**). */
async function restoreLastCompletedFromSessionIfNeeded(
  boot: ParticipantBootstrap,
  quizId: string,
  invitationToken: string,
  signal: AbortSignal,
): Promise<void> {
  if (boot.last_completed_attempt?.status === "completed") {
    return;
  }
  if (boot.in_progress_attempt?.status === "in_progress") {
    return;
  }
  const storedId = storedLastAttemptId(quizId, invitationToken);
  if (storedId === null) {
    return;
  }
  let res: Response;
  try {
    res = await fetchParticipantAttempt(
      quizId,
      storedId,
      invitationToken,
      { signal },
    );
  } catch {
    clearStoredLastAttempt(quizId, invitationToken);
    return;
  }
  if (signal.aborted) {
    return;
  }
  let rawBody: unknown;
  try {
    rawBody = await res.json();
  } catch {
    clearStoredLastAttempt(quizId, invitationToken);
    return;
  }
  if (!(res.ok && isParsableParticipantAttempt(rawBody))) {
    clearStoredLastAttempt(quizId, invitationToken);
    return;
  }
  if (rawBody.attempt_id !== storedId) {
    clearStoredLastAttempt(quizId, invitationToken);
    return;
  }
  boot.last_completed_attempt = rawBody;
}

/** Normalizes snapshot **answers** (legacy int or list) to sorted display index lists. */
function normalizeSnapshotAnswers(
  raw: Record<string, number | number[]> | undefined,
): Record<string, number[]> {
  if (!raw) {
    return {};
  }
  const out: Record<string, number[]> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "number") {
      out[k] = [v];
    } else if (Array.isArray(v)) {
      const xs = v.filter((x) => typeof x === "number");
      out[k] = [...new Set(xs)].sort((a, b) => a - b);
    }
  }
  return out;
}

function normalizedParticipantDisplayName(raw: unknown): string {
  return typeof raw === "string" ? raw.trim() : "";
}

function formatParticipantAssignmentIssuedAt(
  iso: string | null | undefined,
  locale: SupportedLocale,
): string | null {
  if (typeof iso !== "string" || !iso.trim()) {
    return null;
  }
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

/** True when the document was loaded via an explicit reload (keyboard or toolbar refresh). */
function isDocumentReloadNavigation(): boolean {
  if (
    typeof performance === "undefined" ||
    typeof performance.getEntriesByType !== "function"
  ) {
    return false;
  }
  const nav = performance.getEntriesByType(
    "navigation",
  )[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === "reload";
}

function indexOfFirstUnansweredQuestion(
  att: ParticipantAttemptSnapshot,
  answers: Record<string, number[]>,
): number {
  const ordered = att.ordered_questions;
  const total = ordered.length;
  if (total <= 0) {
    return 0;
  }
  for (let i = 0; i < total; i += 1) {
    const picked = answers[ordered[i].question_id];
    if (!picked || picked.length === 0) {
      return i;
    }
  }
  return total - 1;
}

function participantQuestionSelectedIndices(
  snap: ParticipantAttemptSnapshot,
  questionId: string,
): number[] {
  const rawSel = snap.answers[questionId];
  const idxSet =
    typeof rawSel === "number" ? [rawSel] : Array.isArray(rawSel) ? rawSel : [];
  return [...new Set(idxSet)]
    .filter((n): n is number => typeof n === "number" && Number.isInteger(n))
    .sort((a, b) => a - b);
}

function participantOutcomeSelectionSummaryRows(
  snap: ParticipantAttemptSnapshot,
): Array<{
  stem: string;
  choice_labels: string[];
  selected_indices: number[];
}> | null {
  if (snap.status !== "completed") {
    return null;
  }
  const rows: Array<{
    stem: string;
    choice_labels: string[];
    selected_indices: number[];
  }> = [];
  for (const q of snap.ordered_questions) {
    const labels = q.choices;
    if (
      typeof q.stem !== "string" ||
      !Array.isArray(labels) ||
      labels.length === 0
    ) {
      continue;
    }
    rows.push({
      stem: q.stem,
      choice_labels: labels,
      selected_indices: participantQuestionSelectedIndices(snap, q.question_id),
    });
  }
  return rows.length > 0 ? rows : null;
}

function participantOutcomeTranscriptRows(
  snap: ParticipantAttemptSnapshot,
): ParticipantTranscriptReviewRow[] | null {
  if (snap.status !== "completed") {
    return null;
  }
  const out: ParticipantTranscriptReviewRow[] = [];
  for (const q of snap.ordered_questions) {
    const labels = q.choices;
    if (
      typeof q.stem !== "string" ||
      !Array.isArray(labels) ||
      labels.length === 0 ||
      typeof q.answered_correctly !== "boolean"
    ) {
      return null;
    }
    out.push({
      question_id: q.question_id,
      stem: q.stem,
      choice_labels: labels,
      selected_indices: participantQuestionSelectedIndices(snap, q.question_id),
      answered_correctly: q.answered_correctly,
    });
  }
  return out.length > 0 ? out : null;
}

function createParticipantTrainingDocumentsListUl(
  t: Translate,
  quizId: string,
  invitationToken: string,
  documents: ParticipantTrainingDocument[],
): HTMLUListElement {
  const sortedDocs = documents.slice().sort((a, b) => a.ordinal - b.ordinal);
  const docsList = document.createElement("ul");
  docsList.className = "participant-quiz__material-list";

  if (sortedDocs.length === 0) {
    const lone = document.createElement("li");
    lone.className = "participant-quiz__material-empty";
    lone.textContent = t("quiz.participant.training_docs_empty");
    docsList.appendChild(lone);
  } else {
    for (const doc of sortedDocs) {
      const li = document.createElement("li");
      li.className = "participant-quiz__material-doc";
      const a = document.createElement("a");
      a.className = "participant-quiz__material-btn qz-btn qz-btn--secondary";
      a.href = participantTrainingDocumentDownloadUrl(
        quizId,
        doc.training_document_id,
        invitationToken,
      );
      const safeName = doc.original_filename.trim() || doc.training_document_id;
      const extKind = trainingMaterialIconKind(
        normalizedTrainingMaterialExtension(doc.original_filename),
      );
      a.rel = "nofollow";

      const inner = document.createElement("span");
      inner.className = "participant-quiz__material-btn-inner";
      inner.appendChild(createTrainingMaterialFileIcon(extKind));
      const label = document.createElement("span");
      label.className = "participant-quiz__material-btn-label";
      label.textContent = safeName;
      inner.appendChild(label);
      a.appendChild(inner);

      a.setAttribute(
        "aria-label",
        t("quiz.participant.training_doc_download_aria", {
          filename: safeName,
        }),
      );
      li.appendChild(a);
      docsList.appendChild(li);
    }
  }
  return docsList;
}

function appendParticipantResultIdentification(
  mount: HTMLElement,
  t: Translate,
  quizTitle: string,
  participantName: string,
): void {
  const box = document.createElement("div");
  box.className = "participant-quiz__result-ident";

  const titleLine = document.createElement("p");
  titleLine.className = "participant-quiz__result-ident-line";
  const titleLab = document.createElement("span");
  titleLab.className = "participant-quiz__result-ident-label";
  titleLab.textContent = `${t("quiz.participant.result_ident_quiz_label")}\u00a0`;
  titleLine.appendChild(titleLab);
  const titleVal = document.createElement("span");
  titleVal.className = "participant-quiz__result-ident-value";
  titleVal.textContent = quizTitle.trim().length > 0 ? quizTitle.trim() : quizTitle;
  titleLine.appendChild(titleVal);
  box.appendChild(titleLine);

  const nameLine = document.createElement("p");
  nameLine.className = "participant-quiz__result-ident-line";
  const nameLab = document.createElement("span");
  nameLab.className = "participant-quiz__result-ident-label";
  nameLab.textContent = `${t("quiz.participant.result_ident_participant_label")}\u00a0`;
  nameLine.appendChild(nameLab);
  const nameVal = document.createElement("span");
  nameVal.className = "participant-quiz__result-ident-value";
  const trimmed = participantName.trim();
  if (trimmed) {
    nameVal.textContent = trimmed;
  } else {
    nameVal.classList.add("participant-quiz__result-ident-value--placeholder");
    nameVal.textContent = t("quiz.participant.result_ident_participant_not_set");
  }
  nameLine.appendChild(nameVal);
  box.appendChild(nameLine);

  mount.appendChild(box);
}

function isParticipantBootstrap(v: unknown): v is ParticipantBootstrap {
  if (!isRecord(v)) {
    return false;
  }
  if (typeof v.quiz_id !== "string" || typeof v.title !== "string") {
    return false;
  }
  if ("participant_display_name" in v) {
    const p = v.participant_display_name;
    if (p !== null && p !== undefined && typeof p !== "string") {
      return false;
    }
  }
  return Array.isArray(v.training_documents);
}

/**
 * Participant training quiz (**`/quiz/{uuid}`**, invitation **`token`** in query then
 * **`sessionStorage`** in-tab): material, MC attempt, submit, outcome.
 */
export async function renderParticipantQuiz(
  mount: HTMLElement,
  t: Translate,
  options: RenderParticipantQuizOptions,
): Promise<void> {
  mount.replaceChildren();

  const appendParticipantChrome = (page: HTMLElement): void => {
    const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
    header.classList.add("home-header");
    page.appendChild(header);
    void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
      signal: options.signal,
    });
  };

  if (!options.invitationToken.trim()) {
    const shell = document.createElement("div");
    shell.className = "participant-quiz-shell";
    shell.appendChild(
      createSkipLink(t("quiz.participant.skip_to_main"), "participant-main"),
    );

    const page = createPageContainer();
    shell.appendChild(page);

    appendParticipantChrome(page);

    const main = document.createElement("main");
    main.id = "participant-main";
    main.className = "participant-quiz-main";
    main.tabIndex = -1;
    main.appendChild(createHeading(1, t("quiz.participant.error_title")));
    main.appendChild(
      createText(t("quiz.participant.error_missing_token"), { muted: true }),
    );
    page.appendChild(main);

    mount.appendChild(shell);
    return;
  }

  const shell = document.createElement("div");
  shell.className = "participant-quiz-shell";
  shell.appendChild(
    createSkipLink(t("quiz.participant.skip_to_main"), "participant-main"),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  appendParticipantChrome(page);

  const main = document.createElement("main");
  main.id = "participant-main";
  main.className = "participant-quiz-main";
  main.tabIndex = -1;
  page.appendChild(main);

  mount.appendChild(shell);

  const status = createText(t("quiz.participant.loading"), { muted: true });
  main.appendChild(status);

  let boot: ParticipantBootstrap;
  try {
    const res = await fetchParticipantBootstrap(
      options.quizId,
      options.invitationToken,
      {
        signal: options.signal,
      },
    );
    if (options.signal.aborted) {
      return;
    }
    if (!res.ok) {
      main.replaceChildren();
      main.appendChild(createHeading(1, t("quiz.participant.error_title")));
      main.appendChild(
        createText(t("quiz.participant.error_load"), { muted: true }),
      );
      return;
    }
    const raw: unknown = await res.json();
    if (!isParticipantBootstrap(raw)) {
      main.replaceChildren();
      main.appendChild(
        createText(t("quiz.participant.error_load"), { muted: true }),
      );
      return;
    }
    boot = raw;
    await restoreLastCompletedFromSessionIfNeeded(
      boot,
      options.quizId,
      options.invitationToken,
      options.signal,
    );
    if (options.signal.aborted) {
      return;
    }
    if (boot.last_completed_attempt?.status === "completed") {
      storeLastAttemptIdAfterSubmit(
        options.quizId,
        options.invitationToken,
        boot.last_completed_attempt.attempt_id,
      );
    }
  } catch {
    if (options.signal.aborted) {
      return;
    }
    main.replaceChildren();
    main.appendChild(
      createText(t("quiz.participant.error_load"), { muted: true }),
    );
    return;
  }

  main.replaceChildren();

  const title = createHeading(1, boot.title);
  main.appendChild(title);

  let participantDisplayName = normalizedParticipantDisplayName(
    boot.participant_display_name,
  );

  let attempt: ParticipantAttemptSnapshot | null = null;
  type ParticipantPhase =
    | "material"
    | "name"
    | "resume"
    | "quiz"
    | "result";
  let phase: ParticipantPhase = "material";

  const bootOpen = boot.in_progress_attempt;
  const bootLastDone = boot.last_completed_attempt ?? null;

  if (bootOpen?.status === "in_progress") {
    attempt = bootOpen;
    if (participantDisplayName.length === 0) {
      phase = "name";
    } else if (isDocumentReloadNavigation()) {
      phase = "quiz";
    } else {
      phase = "resume";
    }
  } else if (bootLastDone?.status === "completed") {
    attempt = bootLastDone;
    phase = "result";
  } else {
    attempt = null;
    phase = "material";
  }

  /** Answers for the open attempt; cleared when **`attempt`** changes. */
  let quizAnswers: Record<string, number[]> = {};
  let quizQuestionIndex = 0;
  /** After last answered question **Next**, show submit-only pane (no question body). */
  let lastQuestionRevealSubmit = false;

  const stack = createStack({ gap: "lg" });
  main.appendChild(stack);

  const materialSection = document.createElement("section");
  materialSection.className = "participant-quiz__section";
  materialSection.appendChild(
    createHeading(2, t("quiz.participant.training_heading")),
  );

  const trainingIntro = createText(t("quiz.participant.training_intro"), {
    muted: true,
  });
  materialSection.appendChild(trainingIntro);

  const docsList = createParticipantTrainingDocumentsListUl(
    t,
    boot.quiz_id,
    options.invitationToken,
    boot.training_documents,
  );
  materialSection.appendChild(docsList);

  const startBtn = createButton(t("quiz.participant.start_attempt"), {
    variant: "primary",
  });
  materialSection.appendChild(startBtn);

  const nameSection = document.createElement("section");
  nameSection.className =
    "participant-quiz__section participant-quiz__section--name";
  nameSection.hidden = true;
  nameSection.appendChild(createHeading(2, t("quiz.participant.name_heading")));
  nameSection.appendChild(
    createText(t("quiz.participant.name_intro"), { muted: true }),
  );

  const nameInput = createTextInput({
    id: "participant-quiz-name-input",
    name: "participant_display_name",
    placeholder: t("quiz.participant.name_placeholder"),
    value:
      participantDisplayName.length > 0 ? participantDisplayName : undefined,
  });
  nameInput.autocomplete = "name";
  nameSection.appendChild(
    createField({
      id: "participant-quiz-name-input",
      label: t("quiz.participant.name_label"),
      control: nameInput,
      hint: t("quiz.participant.name_hint"),
    }),
  );

  const nameNav = document.createElement("div");
  nameNav.className = "participant-quiz__question-nav";

  const nameBackBtn = createButton(t("quiz.participant.name_back_material"), {
    variant: "secondary",
  });
  const nameContinueBtn = createButton(t("quiz.participant.name_continue"), {
    variant: "primary",
  });
  nameNav.append(nameBackBtn, nameContinueBtn);
  nameSection.appendChild(nameNav);

  const quizSection = document.createElement("section");
  quizSection.className = "participant-quiz__section";
  quizSection.hidden = true;

  const resultSection = document.createElement("section");
  resultSection.className = "participant-quiz__section";
  resultSection.hidden = true;

  stack.append(materialSection, nameSection, quizSection, resultSection);

  const syncQuizAnswersFromAttempt = (): void => {
    if (!attempt || attempt.status !== "in_progress") {
      quizAnswers = {};
      return;
    }
    quizAnswers = normalizeSnapshotAnswers(attempt.answers);
  };

  const handleAbortAttemptClick = (abortBtn: HTMLButtonElement): void => {
    if (!window.confirm(t("quiz.participant.abort_attempt_confirm"))) {
      return;
    }
    void (async () => {
      const cur = attempt;
      if (!cur || cur.status !== "in_progress") {
        return;
      }
      abortBtn.disabled = true;
      const res = await deleteParticipantAttempt(
        options.quizId,
        cur.attempt_id,
        options.invitationToken,
        { signal: options.signal },
      );
      if (options.signal.aborted) {
        abortBtn.disabled = false;
        return;
      }
      let raw: unknown;
      try {
        raw = await res.json();
      } catch {
        abortBtn.disabled = false;
        window.alert(t("quiz.participant.error_load"));
        return;
      }
      if (!res.ok) {
        abortBtn.disabled = false;
        window.alert(t("quiz.participant.error_load"));
        return;
      }
      let lastDone: ParticipantAttemptSnapshot | null = null;
      if (isRecord(raw)) {
        const lc = raw.last_completed_attempt;
        if (lc === null) {
          lastDone = null;
        } else if (isParsableParticipantAttempt(lc)) {
          lastDone = lc;
        }
      }
      boot.in_progress_attempt = null;
      boot.last_completed_attempt = lastDone;
      if (lastDone?.status === "completed") {
        storeLastAttemptIdAfterSubmit(
          options.quizId,
          options.invitationToken,
          lastDone.attempt_id,
        );
        attempt = lastDone;
        showResult();
      } else {
        attempt = null;
        showMaterial();
      }
    })();
  };

  const renderQuiz = (): void => {
    if (!attempt || attempt.status !== "in_progress") {
      return;
    }
    const ordered = attempt.ordered_questions;
    const total = ordered.length;
    quizSection.replaceChildren();

    if (total <= 0) {
      quizSection.appendChild(
        createHeading(2, t("quiz.participant.question_heading")),
      );
      quizSection.appendChild(
        createText(t("quiz.participant.error_load"), { muted: true }),
      );
      return;
    }

    quizQuestionIndex = Math.min(Math.max(quizQuestionIndex, 0), total - 1);

    const isSubmitOnlyStep =
      lastQuestionRevealSubmit && quizQuestionIndex === total - 1;
    if (lastQuestionRevealSubmit && quizQuestionIndex !== total - 1) {
      lastQuestionRevealSubmit = false;
    }

    const wireSubmitAnswers = (sub: HTMLButtonElement): void => {
      sub.addEventListener("click", () => {
        void (async () => {
          for (const qq of attempt!.ordered_questions) {
            const a = quizAnswers[qq.question_id];
            if (!a || a.length === 0) {
              window.alert(t("quiz.participant.answer_all"));
              return;
            }
          }
          sub.disabled = true;
          sub.textContent = t("quiz.participant.submitting");
          const sr = await postParticipantSubmit(
            options.quizId,
            attempt!.attempt_id,
            options.invitationToken,
            { signal: options.signal },
          );
          if (options.signal.aborted) {
            return;
          }
          if (!sr.ok) {
            sub.disabled = false;
            sub.textContent = t("quiz.participant.submit_answers");
            window.alert(t("quiz.participant.error_load"));
            return;
          }
          const body: unknown = await sr.json();
          if (
            isRecord(body) &&
            typeof body.attempt_id === "string" &&
            body.status === "completed"
          ) {
            attempt = body as unknown as ParticipantAttemptSnapshot;
            boot.in_progress_attempt = null;
            boot.last_completed_attempt = attempt;
            storeLastAttemptIdAfterSubmit(
              options.quizId,
              options.invitationToken,
              attempt.attempt_id,
            );
            showResult();
          }
        })();
      });
    };

    if (isSubmitOnlyStep) {
      quizSection.appendChild(createHeading(2, boot.title));

      const issuedLine = formatParticipantAssignmentIssuedAt(
        boot.assignment_issued_at,
        options.locale,
      );
      if (issuedLine) {
        quizSection.appendChild(
          createText(
            t("quiz.participant.submit_assignment_issued", {
              datetime: issuedLine,
            }),
            { muted: true },
          ),
        );
      }

      const navSubmit = document.createElement("div");
      navSubmit.className = "participant-quiz__question-nav";

      const backSubmit = createButton(t("quiz.participant.nav_back"), {
        variant: "secondary",
      });
      backSubmit.addEventListener("click", () => {
        lastQuestionRevealSubmit = false;
        renderQuiz();
      });

      const subOnly = createButton(t("quiz.participant.submit_answers"), {
        variant: "primary",
      });
      wireSubmitAnswers(subOnly);

      const abortSubmit = createButton(t("quiz.participant.abort_attempt"), {
        variant: "secondary",
      });
      abortSubmit.setAttribute(
        "aria-label",
        t("quiz.participant.abort_attempt_aria"),
      );
      abortSubmit.addEventListener("click", () =>
        handleAbortAttemptClick(abortSubmit),
      );

      navSubmit.append(backSubmit, abortSubmit, subOnly);
      quizSection.appendChild(navSubmit);
      return;
    }

    quizSection.appendChild(
      createHeading(2, t("quiz.participant.question_heading")),
    );
    quizSection.appendChild(
      createText(
        t("quiz.participant.progress_label", {
          current: String(quizQuestionIndex + 1),
          total: String(total),
        }),
        { muted: true },
      ),
    );

    const q = ordered[quizQuestionIndex];
    const multi = q.multi_select === true;
    const wrap = document.createElement("div");
    wrap.className = "participant-quiz__question";
    wrap.appendChild(createHeading(3, q.stem));

    const g = document.createElement("div");
    g.className = "qz-mc-group";
    g.setAttribute("role", "group");
    const groupName = `${attempt.attempt_id}-${q.question_id}`;
    const prevSel = quizAnswers[q.question_id] ?? [];

    q.choices.forEach((label, idx) => {
      const tile = createMcOptionTile({
        id: `${groupName}-${idx}`,
        name: multi ? `${groupName}-${idx}` : groupName,
        value: String(idx),
        label,
        variant: multi ? "checkbox" : "radio",
        checked: multi ? prevSel.includes(idx) : prevSel[0] === idx,
      });
      const input = tile.querySelector("input");
      if (input) {
        input.addEventListener("change", () => {
          void (async () => {
            let next: number[];
            if (multi) {
              const cur = new Set(quizAnswers[q.question_id] ?? []);
              if (input.checked) {
                cur.add(idx);
              } else {
                cur.delete(idx);
              }
              next = [...cur].sort((a, b) => a - b);
            } else {
              next = [idx];
            }
            const r = await patchParticipantAttemptSelection(
              options.quizId,
              attempt!.attempt_id,
              options.invitationToken,
              q.question_id,
              next,
              { signal: options.signal },
            );
            if (options.signal.aborted) {
              return;
            }
            if (r.ok) {
              quizAnswers[q.question_id] = next;
            }
          })();
        });
      }
      g.appendChild(tile);
    });
    wrap.appendChild(g);
    quizSection.appendChild(wrap);

    const nav = document.createElement("div");
    nav.className = "participant-quiz__question-nav";

    const backQ = createButton(t("quiz.participant.nav_back"), {
      variant: "secondary",
      disabled: quizQuestionIndex === 0,
    });
    backQ.addEventListener("click", () => {
      if (quizQuestionIndex > 0) {
        quizQuestionIndex -= 1;
        lastQuestionRevealSubmit = false;
        renderQuiz();
      }
    });

    const nextQ = createButton(t("quiz.participant.nav_next"), {
      variant: "primary",
    });
    nextQ.addEventListener("click", () => {
      if (quizQuestionIndex === total - 1) {
        lastQuestionRevealSubmit = true;
        renderQuiz();
        return;
      }
      quizQuestionIndex += 1;
      lastQuestionRevealSubmit = false;
      renderQuiz();
    });

    const abortQ = createButton(t("quiz.participant.abort_attempt"), {
      variant: "secondary",
    });
    abortQ.setAttribute("aria-label", t("quiz.participant.abort_attempt_aria"));
    abortQ.addEventListener("click", () => handleAbortAttemptClick(abortQ));

    nav.append(backQ, abortQ, nextQ);
    quizSection.appendChild(nav);
  };

  const showResult = (): void => {
    phase = "result";
    materialSection.hidden = true;
    nameSection.hidden = true;
    quizSection.hidden = true;
    resultSection.hidden = false;
    resultSection.replaceChildren();
    appendParticipantResultIdentification(
      resultSection,
      t,
      boot.title,
      participantDisplayName,
    );
    const passed = attempt?.passed === true;
    resultSection.appendChild(
      createHeading(
        2,
        passed
          ? t("quiz.participant.result_passed")
          : t("quiz.participant.result_failed"),
      ),
    );
    const rawPass = boot.configuration?.pass_percent;
    const passThreshold =
      typeof rawPass === "number" && Number.isFinite(rawPass)
        ? Math.max(0, Math.min(100, rawPass))
        : 70;
    const passThresholdStr = (): string =>
      passThreshold === Math.round(passThreshold)
        ? String(Math.round(passThreshold))
        : String(Math.round(passThreshold * 10) / 10);
    const sc = attempt?.score;
    const pct = typeof sc === "number" ? Math.round(sc * 1000) / 10 : null;
    if (pct !== null) {
      resultSection.appendChild(
        createText(
          t("quiz.participant.score_label_with_threshold", {
            percent: String(pct),
            passPercent: passThresholdStr(),
          }),
          {
            muted: true,
          },
        ),
      );
    }
    if (attempt?.status === "completed") {
      resultSection.appendChild(
        createHeading(3, t("quiz.participant.result_training_heading")),
      );
      resultSection.appendChild(
        createParticipantTrainingDocumentsListUl(
          t,
          boot.quiz_id,
          options.invitationToken,
          boot.training_documents,
        ),
      );
      const gradedQs = participantOutcomeTranscriptRows(attempt);
      const selectionQs = gradedQs
        ? null
        : participantOutcomeSelectionSummaryRows(attempt);
      if (gradedQs || selectionQs) {
        resultSection.appendChild(
          createHeading(3, t("quiz.participant.result_transcript_heading")),
        );
        if (!gradedQs) {
          resultSection.appendChild(
            createText(
              t("quiz.participant.result_transcript_selection_note"),
              { muted: true },
            ),
          );
        }
        const review = document.createElement("div");
        review.className =
          "participant-quiz__result-review quiz-detail__transcript-body";
        if (gradedQs) {
          renderParticipantTranscriptReview(review, t, gradedQs);
        } else if (selectionQs) {
          renderParticipantTranscriptSelectionOnly(review, t, selectionQs);
        }
        resultSection.appendChild(review);
      }

      const navResult = document.createElement("div");
      navResult.className = "participant-quiz__question-nav";
      const retryBtn = createButton(t("quiz.participant.result_retry"), {
        variant: "primary",
      });
      retryBtn.addEventListener("click", () => {
        void (async () => {
          retryBtn.disabled = true;
          const res = await postParticipantAttempt(
            options.quizId,
            options.invitationToken,
            { signal: options.signal },
          );
          if (options.signal.aborted) {
            retryBtn.disabled = false;
            return;
          }
          if (!res.ok) {
            retryBtn.disabled = false;
            window.alert(t("quiz.participant.error_load"));
            return;
          }
          const body: unknown = await res.json();
          if (
            isRecord(body) &&
            typeof body.attempt_id === "string" &&
            body.status === "in_progress"
          ) {
            attempt = body as unknown as ParticipantAttemptSnapshot;
            boot.in_progress_attempt = attempt;
            boot.last_completed_attempt = null;
            clearStoredLastAttempt(
              options.quizId,
              options.invitationToken,
            );
            showQuiz("new_attempt");
            retryBtn.disabled = false;
            return;
          }
          retryBtn.disabled = false;
          window.alert(t("quiz.participant.error_load"));
        })();
      });
      navResult.appendChild(retryBtn);
      resultSection.appendChild(navResult);
    }
  };

  const showQuiz = (kind: "new_attempt" | "resume_progress"): void => {
    phase = "quiz";
    materialSection.hidden = true;
    nameSection.hidden = true;
    quizSection.hidden = false;
    resultSection.hidden = true;
    syncQuizAnswersFromAttempt();
    if (
      kind === "resume_progress" &&
      attempt &&
      attempt.status === "in_progress"
    ) {
      quizQuestionIndex = indexOfFirstUnansweredQuestion(attempt, quizAnswers);
    } else {
      quizQuestionIndex = 0;
    }
    lastQuestionRevealSubmit = false;
    renderQuiz();
  };

  const showMaterial = (): void => {
    phase = "material";
    trainingIntro.textContent = t("quiz.participant.training_intro");
    materialSection.hidden = false;
    nameSection.hidden = true;
    quizSection.hidden = true;
    resultSection.hidden = true;
    startBtn.disabled = false;
    startBtn.textContent = t("quiz.participant.start_attempt");
  };

  const showResumeLanding = (): void => {
    phase = "resume";
    trainingIntro.textContent = t("quiz.participant.training_resume_intro");
    materialSection.hidden = false;
    nameSection.hidden = true;
    quizSection.hidden = true;
    resultSection.hidden = true;
    startBtn.disabled = false;
    startBtn.textContent = t("quiz.participant.resume_attempt");
  };

  const showName = (): void => {
    phase = "name";
    materialSection.hidden = true;
    nameSection.hidden = false;
    quizSection.hidden = true;
    resultSection.hidden = true;
    nameContinueBtn.disabled = false;
    nameContinueBtn.textContent = t("quiz.participant.name_continue");
  };

  nameBackBtn.addEventListener("click", () => {
    showMaterial();
  });

  nameContinueBtn.addEventListener("click", () => {
    void (async () => {
      const typed = nameInput.value.trim();
      if (!typed) {
        window.alert(t("quiz.participant.name_required"));
        return;
      }
      nameContinueBtn.disabled = true;
      nameContinueBtn.textContent = t("quiz.participant.submitting");
      const res = await postParticipantAttempt(
        options.quizId,
        options.invitationToken,
        {
          signal: options.signal,
          participantDisplayName: typed,
        },
      );
      if (options.signal.aborted) {
        return;
      }
      if (!res.ok) {
        nameContinueBtn.disabled = false;
        nameContinueBtn.textContent = t("quiz.participant.name_continue");
        window.alert(t("quiz.participant.error_load"));
        return;
      }
      const body: unknown = await res.json();
      if (isRecord(body) && typeof body.attempt_id === "string") {
        attempt = body as unknown as ParticipantAttemptSnapshot;
        participantDisplayName = typed;
        boot.participant_display_name = typed;
        boot.in_progress_attempt = attempt;
        showQuiz("new_attempt");
        return;
      }
      nameContinueBtn.disabled = false;
      nameContinueBtn.textContent = t("quiz.participant.name_continue");
    })();
  });

  startBtn.addEventListener("click", () => {
    if (phase === "resume") {
      trainingIntro.textContent = t("quiz.participant.training_intro");
      showQuiz("resume_progress");
      return;
    }
    showName();
  });

  if (phase === "result" && attempt?.status === "completed") {
    showResult();
  } else if (phase === "quiz" && attempt?.status === "in_progress") {
    showQuiz("resume_progress");
  } else if (phase === "name") {
    showName();
  } else if (phase === "resume") {
    showResumeLanding();
  } else {
    showMaterial();
  }
}
